/**
 * PauseMenu.ts
 * Semi-transparent pause overlay — appears when the player presses ESC during play.
 * Shows RESUME and MAIN MENU buttons over the running world.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';

const OVERLAY_COL  = 0x080b10;
const PANEL_BG     = 0x0a1614;
const PANEL_BORDER = 0x1a4a3a;
const ACCENT       = 0x00ffcc;
const BTN_BG       = 0x0d2420;
const BTN_HOVER    = 0x152d27;

export class PauseMenu {
  private readonly container = new Container();
  private screenW = 1280;
  private screenH = 720;

  constructor(
    ui: Container,
    private readonly onResume:   () => void,
    private readonly onSave:     () => void,
    private readonly onMainMenu: () => void,
  ) {
    ui.addChild(this.container);
    this.container.visible = false;
  }

  show(w: number, h: number): void {
    this.screenW = w;
    this.screenH = h;
    this.rebuild();
    this.container.visible = true;
  }

  hide(): void {
    this.container.visible = false;
  }

  resize(w: number, h: number): void {
    this.screenW = w;
    this.screenH = h;
    if (this.container.visible) this.rebuild();
  }

  // ── Private ──────────────────────────────────────────────────────────────

  private rebuild(): void {
    this.container.removeChildren();

    const W = this.screenW, H = this.screenH;
    const cx = W / 2, cy = H / 2;

    // ── Dim overlay ──────────────────────────────────────────────────────────
    const dim = new Graphics();
    dim.beginFill(OVERLAY_COL, 0.78);
    dim.drawRect(0, 0, W, H);
    dim.endFill();
    this.container.addChild(dim);

    // ── Panel ────────────────────────────────────────────────────────────────
    const PW = Math.min(290, W - 32), PH = 300;
    const px = cx - PW / 2, py = cy - PH / 2;

    const panel = new Graphics();
    panel.beginFill(PANEL_BG, 0.97);
    panel.lineStyle(1, PANEL_BORDER, 0.75);
    panel.drawRoundedRect(px, py, PW, PH, 4);
    panel.endFill();
    panel.lineStyle(0);
    this.container.addChild(panel);

    // ── Title ────────────────────────────────────────────────────────────────
    const title = new Text('PAUSED', new TextStyle({
      fontFamily: 'Menlo, Consolas, monospace',
      fontSize:   20,
      fontWeight: 'bold',
      fill:       '#c9fff8',
      letterSpacing: 6,
    }));
    title.anchor.set(0.5, 0);
    title.position.set(cx, py + 28);
    this.container.addChild(title);

    // Thin rule under title
    const rule = new Graphics();
    rule.lineStyle(1, PANEL_BORDER, 0.5);
    rule.moveTo(px + 28, py + 70);
    rule.lineTo(px + PW - 28, py + 70);
    rule.lineStyle(0);
    this.container.addChild(rule);

    // ── Buttons ──────────────────────────────────────────────────────────────
    this.addBtn(cx, py + 112, '▶  RESUME',    ACCENT,   this.onResume);
    this.addBtn(cx, py + 172, '⬛  SAVE',      0xffcc44, this.onSave);
    this.addBtn(cx, py + 232, '⟲  MAIN MENU', 0x6a9390, this.onMainMenu);
  }

  private addBtn(cx: number, cy: number, label: string, colour: number, cb: () => void): void {
    const BW = Math.min(210, this.screenW - 80), BH = 42;

    const g = new Graphics();
    const draw = (hover: boolean) => {
      g.clear();
      g.beginFill(hover ? BTN_HOVER : BTN_BG, 0.95);
      g.lineStyle(1, hover ? colour : PANEL_BORDER, hover ? 0.85 : 0.5);
      g.drawRoundedRect(0, 0, BW, BH, 3);
      g.endFill();
      g.lineStyle(0);
    };
    draw(false);
    g.position.set(cx - BW / 2, cy - BH / 2);
    g.eventMode = 'static';
    g.cursor    = 'pointer';
    g.on('pointerover',  () => draw(true));
    g.on('pointerout',   () => draw(false));
    g.on('pointerdown',  cb);
    this.container.addChild(g);

    const txt = new Text(label, new TextStyle({
      fontFamily: 'Menlo, Consolas, monospace',
      fontSize:   13,
      fontWeight: 'bold',
      fill:       `#${colour.toString(16).padStart(6, '0')}`,
      letterSpacing: 2,
    }));
    txt.anchor.set(0.5, 0.5);
    txt.position.set(cx, cy);
    txt.eventMode = 'none';
    this.container.addChild(txt);
  }
}
